import { depascalize, DropdownOption, getEnumValues, NamedProperty } from "sprint-common-web";

import { AspectRatio } from "../../../common/data/lookup-data-providers/AspectRatiosDataProvider";
import { EncodeSpecificationResponse } from "../../../common/api/orders-api/encode-specification/responses/EncodeSpecificationResponse";
import { AuditInfo } from "../../../common/types/AuditInfo";
import { ManzanitaPresetReference } from "../../../common/types/ManzanitaPresetReference";

export type EncodeSpecificationItem = EncodeSpecificationMpeg1Item | EncodeSpecificationMpeg4Item;

interface BaseEncodeSpecificationItem {
    id: string;
    name: string;
    audit: AuditInfo;
    isModified?: boolean;
    targetAspectRatio: AspectRatio;
}

export enum EncodeSpecificationType {
    MPEG_1 = 1,
    MPEG_4 = 2
}

export enum AspectType {
    FullFrame = 1,
    Letterbox = 2
}

export const aspectTypesOptions = getEnumValues(AspectType).map<DropdownOption>((type) => ({
    key: type.toString(),
    text: depascalize(AspectType[type])
}));

export type EncodeSpecificationFramerate = typeof encodeSpecificationFramerates[number];

const OTHER_FRAMERATE = "OTHER";

export const encodeSpecificationFramerates = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60, OTHER_FRAMERATE] as const;

export interface EncodeSpecificationMpeg1Item extends BaseEncodeSpecificationItem {
    type: EncodeSpecificationType.MPEG_1;
    frameratePresets: FrameratePresets;
    aspectType: AspectType;
    slug?: number;
}

export type FrameratePresets = Record<EncodeSpecificationFramerate, ManzanitaPresetReference>;

export interface EncodeSpecificationMpeg4Item extends BaseEncodeSpecificationItem {
    type: EncodeSpecificationType.MPEG_4;
    ffmpegCommand: string;
    framerateProfile: NamedProperty;
}

export const mapToEncodeSpecificationItem = (item: EncodeSpecificationResponse): EncodeSpecificationItem => {
    switch (item.type) {
        case EncodeSpecificationType.MPEG_1: {
            return {
                id: item.id,
                name: item.name,
                type: item.type,
                frameratePresets: item.frameratePresets,
                aspectType: item.aspectType,
                slug: item.slug,
                audit: item.audit,
                targetAspectRatio: item.targetAspectRatio
            };
        }

        case EncodeSpecificationType.MPEG_4: {
            return {
                id: item.id,
                name: item.name,
                type: item.type,
                ffmpegCommand: item.ffmpegCommand,
                audit: item.audit,
                framerateProfile: item.framerateProfile,
                targetAspectRatio: item.targetAspectRatio
            };
        }

        default:
            throw new Error("Incorrect encode specification type");
    }
};
